import { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@digital-services/ui";

import { maxLength } from "@validators/MaxLengthValidator";
import { isNotEmpty } from "@validators/NotEmptyValidator";
import { isNotRegExMatch } from "@validators/RegExValidator";

import { useInput } from "@hooks/useInput";
import { OnInputChangeFunctor } from "@utils/validators/models/ValidationResult";

interface Props {
  id: string;
  label: string;
  initialNameValue: string;
  onDisplayNameChange?: OnInputChangeFunctor;
  disabled?: boolean;
}

// Note that this component is used in places where Context usage is expected
export const DisplayNameExtended: FunctionComponent<Props> = props => {
  const { id, label, initialNameValue, onDisplayNameChange, disabled } = props;

  const { t } = useTranslation();
  const { t: tCommon } = useTranslation();

  const validator1 = maxLength(256, () =>
    tCommon("validation.string.max-length", {
      fieldName: label,
      maxLength: 256
    })
  );

  const validator2 = isNotEmpty(() =>
    t("validation.string.not-empty", { fieldName: label })
  );

  const validator3 = isNotRegExMatch(/[<>]/, () =>
    t("validation.string.illegal-characters", {
      fieldName: label,
      characters: "<, >"
    })
  );

  const { isValid, errorMessage, valueChangeHandler } = useInput(
    [validator1, validator2, validator3],
    initialNameValue
  );

  useEffect(() => {
    if (initialNameValue) {
      // We need this initial validation for this component when value is passed from context
      valueChangeHandler(initialNameValue);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNameValue]);

  const changeHandler = event => {
    const val = event.target.value;
    const { value, isValid: fastIsValid } = valueChangeHandler(val);

    if (onDisplayNameChange) {
      onDisplayNameChange(value, fastIsValid);
    }
  };

  return (
    <Input
      type="text"
      id={id}
      data-testid={id}
      label={label}
      autoComplete={disabled ? "off" : "name"} // string value should be standard HTML value
      error={!isValid}
      helperText={!isValid ? errorMessage : undefined}
      value={initialNameValue}
      disabled={disabled}
      onBlur={changeHandler}
      onChange={changeHandler}
    />
  );
};
